import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Trash2, Download, ShieldCheck } from "lucide-react";
import { adminLogin, fetchLeads, updateLeadStatus, deleteLead } from "../lib/api";

const STATUSES = ["all", "new", "contacted", "qualified", "closed"];
const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  qualified: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-600",
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t, status) => {
    setLoading(true);
    try {
      const { data } = await fetchLeads(t, status);
      setLeads(data.leads);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setToken("");
        toast.error("Session expired — please log in again.");
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token, filter);
  }, [token, filter, load]);

  const onLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError("");
    try {
      const { data } = await adminLogin(email, password);
      sessionStorage.setItem("admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Login failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await updateLeadStatus(token, id, status);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    try {
      await deleteLead(token, id);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Could not delete lead");
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Phone", "Email", "Service", "City", "Status", "Source", "Date"],
      ...leads.map((l) => [l.name, l.phone, l.email, l.service, l.city, l.status, l.source_page, l.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "complisure-leads.csv";
    a.click();
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-ice/60 px-4">
        <form data-testid="admin-login-form" onSubmit={onLogin} className="glass-strong w-full max-w-md rounded-3xl p-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-brand-sky text-white shadow-lg shadow-brand-blue/25">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-brand-navy">Admin — Leads Dashboard</h1>
          <p className="mt-1.5 text-sm text-brand-gray">Complisure Counsultancy internal access only.</p>
          <div className="mt-6 space-y-4">
            <input data-testid="admin-email" type="email" required placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-brand-blue/15 bg-white/80 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-blue/40" />
            <input data-testid="admin-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-brand-blue/15 bg-white/80 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-blue/40" />
            {authError && <p data-testid="admin-login-error" className="text-sm font-medium text-red-600">{authError}</p>}
            <button data-testid="admin-login-submit" disabled={authLoading}
              className="btn-gradient inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-60">
              {authLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-ice/60 py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">Leads Dashboard</h1>
            <p className="mt-1 text-sm text-brand-gray">{leads.length} lead{leads.length === 1 ? "" : "s"} {filter !== "all" ? `· filtered: ${filter}` : "· total"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={() => load(token, filter)} className="glass flex h-10 w-10 items-center justify-center rounded-full text-brand-navy hover:bg-white" aria-label="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button data-testid="admin-export" onClick={exportCsv} className="glass inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-brand-navy hover:bg-white">
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button data-testid="admin-logout" onClick={() => { sessionStorage.removeItem("admin_token"); setToken(""); }} className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-blue">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button key={s} data-testid={`admin-filter-${s}`} onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-2 text-xs font-semibold capitalize transition-all ${filter === s ? "bg-brand-blue text-white shadow-lg shadow-brand-blue/25" : "glass text-brand-navy hover:bg-white"}`}>
              {s}
            </button>
          ))}
        </div>

        <div data-testid="admin-leads-table" className="glass mt-6 overflow-x-auto rounded-3xl">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-brand-blue/10 text-xs uppercase tracking-wider text-brand-gray">
                <th className="px-5 py-4">Lead</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Service</th>
                <th className="px-5 py-4">City</th>
                <th className="px-5 py-4">Source</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id.slice(0, 8)}`} className="border-b border-brand-blue/5 align-top transition-colors hover:bg-white/60">
                  <td className="px-5 py-4">
                    <p className="font-semibold text-brand-navy">{l.name}</p>
                    <p className="text-xs text-brand-gray">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
                    {l.message && <p className="mt-1 max-w-[220px] text-xs italic text-brand-gray">"{l.message}"</p>}
                  </td>
                  <td className="px-5 py-4">
                    <a href={`tel:${l.phone}`} className="font-medium text-brand-blue">{l.phone}</a>
                    <p className="text-xs text-brand-gray">{l.email}</p>
                  </td>
                  <td className="px-5 py-4 text-brand-navy/80">{l.service}</td>
                  <td className="px-5 py-4 text-brand-navy/80">{l.city || "—"}</td>
                  <td className="px-5 py-4 text-xs text-brand-gray">{l.source_page || "—"}</td>
                  <td className="px-5 py-4">
                    <select data-testid={`lead-status-${l.id.slice(0, 8)}`} value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}
                      className={`cursor-pointer rounded-full px-3 py-1.5 text-xs font-semibold capitalize outline-none ${STATUS_STYLES[l.status]}`}>
                      {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <button data-testid={`lead-delete-${l.id.slice(0, 8)}`} onClick={() => remove(l.id)} aria-label="Delete lead" className="rounded-full p-2 text-brand-gray transition-colors hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && !loading && (
                <tr><td colSpan={7} data-testid="leads-empty" className="px-5 py-16 text-center text-brand-gray">No leads yet. They'll appear here the moment a form is submitted.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
